import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { X, Info, AlertTriangle, OctagonAlert } from "lucide-react";
import { siteBanner, siteBannerSeen, clientAdvisorNotice, advisorNoticeSeen } from "../lib/api";
import { tr, useLang } from "../lib/i18n";
import { getHousehold } from "../lib/household";

export const BANNER_LEVELS = {
  info: { cls: "bg-sky-700 text-white", Icon: Info, label: "Info" },
  warning: { cls: "bg-amber-400 text-amber-950", Icon: AlertTriangle, label: "Warning" },
  outage: { cls: "bg-red-700 text-white", Icon: OctagonAlert, label: "Outage" },
};
const KEY = "hv_banner_closed";
const VID = "hv_banner_vid";

const visitor = () => {
  try {
    let v = localStorage.getItem(VID);
    if (!v) { v = `v${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`; localStorage.setItem(VID, v); }
    return v;
  } catch { return "anonymous-visitor"; }
};
const pinged = new Set();
const ping = (version, kind, path = "") => {
  const k = `${version}|${kind}|${path}`;
  if (kind === "view" && pinged.has(k)) return;
  pinged.add(k);
  siteBannerSeen({ version, visitor: visitor(), kind }).catch(() => {});
};

export const BannerBar = ({ b, onClose, spanish = false, onLink }) => {
  const { cls, Icon } = BANNER_LEVELS[b.level] || BANNER_LEVELS.info;
  const ext = /^https?:/.test(b.link_url || "");
  const msg = spanish && b.message_es ? b.message_es : tr(b.message);
  return (
    <div className={cls} role={b.level === "outage" ? "alert" : "status"} data-testid={`site-banner-${b.level}`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3 text-[13px] sm:text-sm">
        <Icon className="h-4 w-4 shrink-0" />
        <p className="flex-1 min-w-0 leading-snug" data-testid="site-banner-message">
          {msg}
          {b.link_url && (ext
            ? <a href={b.link_url} target="_blank" rel="noreferrer" onClick={onLink} className="ml-2 underline underline-offset-2 font-medium" data-testid="site-banner-link">{tr(b.link_label || "Learn more")}</a>
            : <Link to={b.link_url} onClick={onLink} className="ml-2 underline underline-offset-2 font-medium" data-testid="site-banner-link">{tr(b.link_label || "Learn more")}</Link>)}
        </p>
        {b.dismissible && onClose && <button type="button" onClick={onClose} aria-label={tr("Dismiss")} data-testid="site-banner-dismiss" className="rounded-full h-8 w-8 grid place-items-center hover:bg-black/10 transition-colors shrink-0"><X className="h-4 w-4" /></button>}
      </div>
    </div>
  );
};

// Site-wide notice from Admin → Site banner (or an open Status notice); place = "public" | "app". Counts views/closes.
export const SiteBanner = ({ place }) => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const { data } = useQuery({ queryKey: ["site-banner"], queryFn: siteBanner, refetchInterval: 120000, retry: false });
  const [closed, setClosed] = useState(() => { try { return localStorage.getItem(KEY) || ""; } catch { return ""; } });
  const b = data?.banner;
  const show = !!b && !!b.where?.[place] && !(b.dismissible && closed === b.version);
  useEffect(() => { if (show) ping(b.version, "view", `${place}${pathname}`); }, [show, b?.version, pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!show) return null;
  const close = () => { setClosed(b.version); ping(b.version, "close"); try { localStorage.setItem(KEY, b.version); } catch { /* private mode */ } };
  return <BannerBar b={b} onClose={close} spanish={lang === "es"} onLink={() => ping(b.version, "click")} />;
};

// Inside a client's vault: the notice their advisor posted (advisor portal → Client notice).
export const AdvisorNotice = () => {
  const { lang } = useLang();
  const { data } = useQuery({ queryKey: ["advisor-notice", getHousehold()], queryFn: clientAdvisorNotice, refetchInterval: 300000, retry: false });
  const [closed, setClosed] = useState(() => { try { return localStorage.getItem(`${KEY}_adv`) || ""; } catch { return ""; } });
  const n = data?.notice;
  const show = !!n && !(n.dismissible && closed === n.version);
  useEffect(() => { if (show) advisorNoticeSeen(n.version, "view").catch(() => {}); }, [show, n?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!show) return null;
  const close = () => { setClosed(n.version); advisorNoticeSeen(n.version, "close").catch(() => {}); try { localStorage.setItem(`${KEY}_adv`, n.version); } catch { /* private mode */ } };
  return <div data-testid="advisor-notice"><BannerBar b={{ ...n, message: `${n.firm}: ${n.message}`, message_es: n.message_es ? `${n.firm}: ${n.message_es}` : "" }} onClose={close} spanish={lang === "es"} /></div>;
};
